import { useState, useEffect } from 'react';
import { templateList, getOnboardingTheme, fontOptions, pageContents, siteMapNodes, type ThemeData, type PageContent, type SiteNode } from '@/mocks/websiteBuilder';
import api from '@/services/api';
import { usePageTracking } from '../../../hooks/usePageTracking';


interface DesignTemplateProps {
  selectedTemplate: string;
  onSelectTemplate: (id: string) => void;
  theme: ThemeData;
  onThemeChange: (theme: ThemeData) => void;
  onShowPreview: () => void;
  brandName: string;
  onBrandNameChange: (name: string) => void;
  brandLogo: string | null;
  onBrandLogoChange: (logo: string | null) => void;
  brandTheme?: Partial<ThemeData> | null;
  globalContactInfo?: any;
  pages?: PageContent[];
  isExported?: boolean;
  onRequestUnlock?: () => void;
}

function parseHeroSlide(raw: string) {
  try {
    return JSON.parse(raw) as { headline: string; sub: string; cta: string; image: string };
  } catch {
    return { headline: raw, sub: '', cta: 'Learn More', image: '' };
  }
}

function flattenPages(nodes: SiteNode[]): SiteNode[] {
  const out: SiteNode[] = [];
  for (const n of nodes) {
    out.push(n);
    if (n.children) out.push(...n.children);
  }
  return out;
}

export default function DesignTemplate({ 
  selectedTemplate, 
  onSelectTemplate, 
  theme, 
  onThemeChange, 
  onShowPreview,
  brandName,
  onBrandNameChange,
  brandLogo,
  onBrandLogoChange,
  globalContactInfo,
  pages,
  brandTheme,
  isExported,
  onRequestUnlock
}: DesignTemplateProps) {
  const checkLock = () => {
    if (isExported && onRequestUnlock) {
      onRequestUnlock();
      return true;
    }
    return false;
  };
  const [activeCategory, setActiveCategory] = useState<string>('All');
  usePageTracking({
    moduleName: 'Website Builder',
    submoduleName: `Design & Themes / ${activeCategory}`,
    routePath: `/website-builder#design/${activeCategory.toLowerCase()}`,
  });
  const [overrides, setOverrides] = useState<Set<keyof ThemeData>>(new Set());
  const [templatePanelOpen, setTemplatePanelOpen] = useState(true);
  const [colorSource, setColorSource] = useState<'brand' | 'template'>(brandTheme ? 'brand' : 'template');

  const categories = ['All', 'General', 'Restaurant', 'Salon', 'Clinic', 'Retail', 'Professional'];

  const filteredTemplates = activeCategory === 'All'
    ? templateList
    : templateList.filter(t => t.category === activeCategory);

  const handleColorChange = (key: keyof ThemeData, value: string) => {
    if (checkLock()) return;
    setOverrides(prev => new Set(prev).add(key));
    onThemeChange(prev => ({ ...prev, [key]: value }));
  };

  const handleColorSourceChange = (source: 'brand' | 'template') => {
    if (checkLock()) return;
    setColorSource(source);
    const next = { ...theme };
    let changed = false;
    if (source === 'brand' && brandTheme) {
      (['primary', 'accent', 'background', 'text', 'cta'] as const).forEach(key => {
        if (!overrides.has(key) && brandTheme[key]) {
          next[key] = brandTheme[key] as string;
          changed = true;
        }
      });
    } else if (source === 'template') {
      const t = templateList.find(t => t.id === selectedTemplate);
      if (t?.palette) {
        (['primary', 'accent', 'background', 'text', 'cta'] as const).forEach(key => {
          if (!overrides.has(key)) {
            next[key] = t.palette![key];
            changed = true;
          }
        });
      }
    }
    if (changed) {
      onThemeChange(next);
    }
  };

  const colorFields: { key: keyof ThemeData; label: string; fromOnboarding: boolean }[] = [
    { key: 'primary', label: 'Primary', fromOnboarding: true },
    { key: 'accent', label: 'Accent', fromOnboarding: true },
    { key: 'background', label: 'BG', fromOnboarding: false },
    { key: 'text', label: 'Text', fromOnboarding: false },
    { key: 'cta', label: 'CTA', fromOnboarding: false },
  ];

  const fontFields: { key: keyof ThemeData; label: string; fromOnboarding: boolean }[] = [
    { key: 'headingFont', label: 'Heading', fromOnboarding: true },
    { key: 'bodyFont', label: 'Body', fromOnboarding: true },
  ];

  // Template-specific preview overrides
  const templateOverrides: Record<string, {
    hero?: { headline: string; sub: string; cta: string; image: string };
    usp?: { title: string; description: string; icon: string }[];
    story?: string;
    grid?: { title: string; description: string; icon: string }[];
    gallery?: string[];
    blog?: { title: string; excerpt: string; date: string }[];
  }> = {
    t2: {
      hero: {
        headline: 'Exquisite Dining Awaits',
        sub: 'Farm-to-table cuisine in an intimate setting',
        cta: 'Reserve a Table',
        image: 'https://readdy.ai/api/search-image?query=Elegant%20restaurant%20interior%20with%20warm%20ambient%20lighting%2C%20fine%20dining%20table%20setup%2C%20soft%20bokeh%20background%2C%20warm%20gold%20and%20cream%20tones%2C%20editorial%20food%20photography%20style%2C%20luxurious%20atmosphere%2C%20no%20text%20overlays&width=1400&height=600&seq=t2hero&orientation=landscape',
      },
      usp: [
        { title: 'Farm Fresh', description: 'Ingredients sourced from local organic farms daily.', icon: 'ri-seedling-line' },
        { title: 'Master Chefs', description: 'Award-winning culinary team with Michelin experience.', icon: 'ri-fire-line' },
        { title: 'Private Dining', description: 'Intimate spaces for celebrations and business dinners.', icon: 'ri-vip-crown-line' },
      ],
      story: 'Founded in 2018, Bistro Table began with a simple vision: bring the authenticity of farm kitchens to the city center. Every dish tells the story of the farmer who grew it, the chef who perfected it, and the guest who savors it.',
      grid: [
        { title: 'Seasonal Tasting', description: 'A seven-course journey through the seasons.', icon: 'ri-restaurant-line' },
        { title: 'Wine Pairing', description: 'Curated selections from boutique vineyards.', icon: 'ri-goblet-line' },
        { title: 'Event Catering', description: 'Bespoke menus for weddings and corporate events.', icon: 'ri-calendar-event-line' },
      ],
      gallery: [
        'https://readdy.ai/api/search-image?query=Beautifully%20plated%20gourmet%20dish%20on%20white%20ceramic%20plate%2C%20fine%20dining%20presentation%2C%20warm%20ambient%20restaurant%20lighting%2C%20editorial%20food%20photography%2C%20cream%20and%20gold%20tones&width=400&height=300&seq=t2g1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Elegant%20restaurant%20dining%20room%20interior%20with%20soft%20candlelight%2C%20warm%20wood%20and%20cream%20decor%2C%20intimate%20atmosphere%2C%20editorial%20interior%20photography&width=400&height=300&seq=t2g2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Close-up%20of%20artisan%20bread%20and%20olive%20oil%20on%20rustic%20wooden%20board%2C%20warm%20kitchen%20lighting%2C%20editorial%20food%20photography%2C%20cream%20and%20gold%20tones&width=400&height=300&seq=t2g3&orientation=landscape',
      ],
      blog: [
        { title: 'Spring Menu Highlights', excerpt: 'Discover the fresh flavors of our new seasonal tasting menu.', date: 'May 5, 2025' },
        { title: 'Meet Chef Antonio', excerpt: 'The creative mind behind our signature farm-to-table concept.', date: 'Apr 20, 2025' },
      ],
    },
    t3: {
      hero: {
        headline: 'Transform Your Look',
        sub: 'Luxury salon services tailored to your unique style',
        cta: 'Book Appointment',
        image: 'https://readdy.ai/api/search-image?query=Luxury%20hair%20salon%20interior%20with%20warm%20lighting%2C%20elegant%20mirrors%20and%20styling%20chairs%2C%20soft%20pink%20and%20cream%20tones%2C%20editorial%20beauty%20photography%2C%20sophisticated%20atmosphere%2C%20no%20text%20overlays&width=1400&height=600&seq=t3hero&orientation=landscape',
      },
      usp: [
        { title: 'Expert Stylists', description: 'Internationally trained team with celebrity clientele.', icon: 'ri-vip-diamond-line' },
        { title: 'Premium Products', description: 'Organic, cruelty-free color and treatment lines.', icon: 'ri-heart-pulse-line' },
        { title: 'Relaxing Ambience', description: 'Spa-like environment with complimentary refreshments.', icon: 'ri-drop-line' },
      ],
      story: 'Glamour Chair was founded on the belief that everyone deserves to feel confident in their own skin. Our salon is more than a place for hair — it is a sanctuary where transformation happens, one strand at a time.',
      grid: [
        { title: 'Precision Cuts', description: 'Custom cuts that frame your face and fit your lifestyle.', icon: 'ri-scissors-line' },
        { title: 'Color Mastery', description: 'Balayage, highlights, and full color with premium pigments.', icon: 'ri-palette-line' },
        { title: 'Hair Treatments', description: 'Keratin, deep conditioning, and scalp therapy services.', icon: 'ri-drop-line' },
      ],
      gallery: [
        'https://readdy.ai/api/search-image?query=Elegant%20woman%20with%20styled%20hair%20in%20luxury%20salon%20chair%2C%20warm%20lighting%2C%20soft%20pink%20and%20cream%20tones%2C%20editorial%20beauty%20photography%2C%20professional%20styling&width=400&height=300&seq=t3g1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Premium%20hair%20care%20products%20arranged%20on%20marble%20surface%2C%20soft%20pink%20and%20cream%20aesthetic%2C%20editorial%20product%20photography%2C%20luxury%20beauty%20items&width=400&height=300&seq=t3g2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Close-up%20of%20hands%20styling%20hair%20with%20professional%20tools%2C%20warm%20salon%20lighting%2C%20soft%20pink%20and%20cream%20tones%2C%20editorial%20beauty%20photography&width=400&height=300&seq=t3g3&orientation=landscape',
      ],
      blog: [
        { title: '2025 Hair Trends', excerpt: 'The cuts and colors defining this year\'s style landscape.', date: 'May 1, 2025' },
        { title: 'Protecting Color-Treated Hair', excerpt: 'Expert tips to keep your salon color vibrant longer.', date: 'Apr 15, 2025' },
      ],
    },
    t4: {
      hero: {
        headline: 'Compassionate Care, Advanced Medicine',
        sub: 'Your health is our highest priority',
        cta: 'Book a Consultation',
        image: 'https://readdy.ai/api/search-image?query=Modern%20medical%20clinic%20interior%20with%20clean%20white%20and%20soft%20blue%20tones%2C%20welcoming%20reception%20area%2C%20natural%20light%2C%20editorial%20healthcare%20photography%2C%20calm%20and%20trustworthy%20atmosphere%2C%20no%20text%20overlays&width=1400&height=600&seq=t4hero&orientation=landscape',
      },
      usp: [
        { title: 'Board-Certified', description: 'Every physician is double-board-certified in their specialty.', icon: 'ri-shield-star-line' },
        { title: 'Same-Day Appointments', description: 'Book online and see a doctor within hours, not weeks.', icon: 'ri-time-line' },
        { title: 'Holistic Approach', description: 'We treat the whole person, not just the symptoms.', icon: 'ri-heart-3-line' },
      ],
      story: 'Healing Touch Clinic was established to bridge the gap between advanced medical technology and genuinely compassionate care. We believe that healing begins the moment you walk through our doors.',
      grid: [
        { title: 'General Medicine', description: 'Comprehensive primary care for adults and families.', icon: 'ri-stethoscope-line' },
        { title: 'Specialist Care', description: 'Cardiology, dermatology, orthopedics, and more.', icon: 'ri-hospital-line' },
        { title: 'Preventive Health', description: 'Annual checkups, screenings, and wellness programs.', icon: 'ri-shield-check-line' },
      ],
      gallery: [
        'https://readdy.ai/api/search-image?query=Modern%20doctor%20consultation%20room%20with%20clean%20white%20and%20soft%20blue%20tones%2C%20medical%20equipment%2C%20natural%20light%2C%20editorial%20healthcare%20photography%2C%20calm%20atmosphere&width=400&height=300&seq=t4g1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Friendly%20healthcare%20professional%20greeting%20patient%20at%20reception%20desk%2C%20clean%20modern%20clinic%20interior%2C%20soft%20blue%20and%20white%20tones%2C%20editorial%20healthcare%20photography&width=400&height=300&seq=t4g2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Advanced%20medical%20diagnostic%20equipment%20in%20clean%20modern%20lab%2C%20soft%20blue%20and%20white%20lighting%2C%20editorial%20healthcare%20photography%2C%20professional%20atmosphere&width=400&height=300&seq=t4g3&orientation=landscape',
      ],
      blog: [
        { title: 'Understanding Preventive Care', excerpt: 'Why annual screenings save lives and reduce long-term costs.', date: 'May 3, 2025' },
        { title: 'Mental Health Awareness', excerpt: 'Breaking the stigma and finding support that works for you.', date: 'Apr 22, 2025' },
      ],
    },
    t5: {
      hero: {
        headline: 'Curated Collections for Modern Living',
        sub: 'Discover products that elevate your everyday',
        cta: 'Shop Now',
        image: 'https://readdy.ai/api/search-image?query=Modern%20retail%20storefront%20with%20elegant%20product%20display%2C%20warm%20ambient%20lighting%2C%20clean%20minimalist%20design%2C%20cream%20and%20charcoal%20tones%2C%20editorial%20retail%20photography%2C%20inviting%20shopping%20atmosphere%2C%20no%20text%20overlays&width=1400&height=600&seq=t5hero&orientation=landscape',
      },
      usp: [
        { title: 'Free Shipping', description: 'Complimentary delivery on all orders over $50.', icon: 'ri-truck-line' },
        { title: 'Easy Returns', description: '30-day hassle-free returns with prepaid labels.', icon: 'ri-refresh-line' },
        { title: 'Curated Edit', description: 'Every item is handpicked by our style experts.', icon: 'ri-shopping-bag-3-line' },
      ],
      story: 'Shelf & Cart started as a small curation project in 2019 and has grown into a destination for discerning shoppers. We partner with independent makers and sustainable brands to bring you products with purpose.',
      grid: [
        { title: 'Home & Living', description: 'Minimalist decor, textiles, and functional art pieces.', icon: 'ri-home-smile-line' },
        { title: 'Fashion', description: 'Timeless wardrobe staples from ethical manufacturers.', icon: 'ri-t-shirt-line' },
        { title: 'Wellness', description: 'Self-care essentials for body, mind, and space.', icon: 'ri-leaf-line' },
      ],
      gallery: [
        'https://readdy.ai/api/search-image?query=Elegant%20lifestyle%20product%20flat%20lay%20on%20marble%20surface%2C%20minimalist%20home%20decor%20items%2C%20warm%20cream%20and%20charcoal%20tones%2C%20editorial%20product%20photography%2C%20luxury%20aesthetic&width=400&height=300&seq=t5g1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Modern%20retail%20clothing%20rack%20with%20curated%20garments%2C%20warm%20ambient%20lighting%2C%20cream%20and%20charcoal%20tones%2C%20editorial%20fashion%20photography%2C%20boutique%20aesthetic&width=400&height=300&seq=t5g2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Premium%20wellness%20products%20arranged%20on%20wooden%20shelf%2C%20clean%20minimalist%20styling%2C%20cream%20and%20charcoal%20tones%2C%20editorial%20product%20photography&width=400&height=300&seq=t5g3&orientation=landscape',
      ],
      blog: [
        { title: 'Spring Style Guide', excerpt: 'The essentials you need to refresh your wardrobe this season.', date: 'May 4, 2025' },
        { title: 'Sustainable Shopping Tips', excerpt: 'How to build a conscious closet without sacrificing style.', date: 'Apr 18, 2025' },
      ],
    },
    t6: {
      hero: {
        headline: 'Strategic Excellence for Ambitious Leaders',
        sub: 'Advisory services that drive measurable outcomes',
        cta: 'Schedule a Strategy Call',
        image: 'https://readdy.ai/api/search-image?query=Executive%20boardroom%20with%20panoramic%20city%20view%2C%20dark%20navy%20and%20gold%20accents%2C%20sleek%20modern%20furniture%2C%20warm%20ambient%20lighting%2C%20editorial%20corporate%20photography%2C%20premium%20professional%20atmosphere%2C%20no%20text%20overlays&width=1400&height=600&seq=t6hero&orientation=landscape',
      },
      usp: [
        { title: 'Fortune 500 Experience', description: 'Senior consultants with decades at top-tier firms.', icon: 'ri-briefcase-4-line' },
        { title: 'Data-Driven', description: 'Every recommendation backed by rigorous analytics.', icon: 'ri-bar-chart-grouped-line' },
        { title: 'Bespoke Solutions', description: 'No off-the-shelf playbooks. Strategies tailored to you.', icon: 'ri-fingerprint-line' },
      ],
      story: 'Boardroom was founded by former McKinsey and Bain partners who saw a gap in the market: boutique advisory quality at accessible engagement models. We serve founders, PE firms, and corporate leaders who demand precision without bureaucracy.',
      grid: [
        { title: 'Growth Strategy', description: 'Market entry, expansion, and revenue acceleration plans.', icon: 'ri-line-chart-line' },
        { title: 'Operational Excellence', description: 'Process optimization and cost-structure transformation.', icon: 'ri-settings-3-line' },
        { title: 'M&A Advisory', description: 'Due diligence, valuation, and integration support.', icon: 'ri-hand-coin-line' },
      ],
      gallery: [
        'https://readdy.ai/api/search-image?query=Professional%20business%20team%20meeting%20in%20modern%20glass%20conference%20room%2C%20dark%20navy%20and%20gold%20accents%2C%20warm%20lighting%2C%20editorial%20corporate%20photography%2C%20executive%20atmosphere&width=400&height=300&seq=t6g1&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Close-up%20of%20executive%20hands%20reviewing%20financial%20documents%20on%20sleek%20desk%2C%20dark%20navy%20and%20gold%20accents%2C%20warm%20lighting%2C%20editorial%20corporate%20photography&width=400&height=300&seq=t6g2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20lobby%20with%20marble%20floors%20and%20gold%20accents%2C%20panoramic%20city%20view%2C%20dark%20navy%20walls%2C%20editorial%20architecture%20photography%2C%20luxury%20business%20environment&width=400&height=300&seq=t6g3&orientation=landscape',
      ],
      blog: [
        { title: 'Q2 Market Outlook', excerpt: 'Key trends shaping M&A activity and growth capital in 2025.', date: 'May 6, 2025' },
        { title: 'Operational Leverage', excerpt: 'How mid-market companies can scale without breaking.', date: 'Apr 25, 2025' },
      ],
    },
  };
  const override = templateOverrides[selectedTemplate];


  const homePage = (pages && pages.find((p) => p.pageId === 'home')) || (pageContents.find((p) => p.pageId === 'home') as PageContent);
  const heroSection = homePage?.sections.find((s) => s.type === 'hero');
  const heroSlides = Array.isArray(heroSection?.content)
    ? heroSection.content.map(parseHeroSlide)
    : [];
  const firstHero = override?.hero || (heroSlides[0] || { headline: 'Welcome', sub: '', cta: 'Get Started', image: '' });

  const uspSection = homePage?.sections.find((s) => s.type === 'usp');
  const uspCards = override?.usp || (Array.isArray(uspSection?.content)
    ? uspSection.content.map((c) => {
        try {
          return JSON.parse(c);
        } catch {
          return { title: c, description: '', icon: 'ri-star-line' };
        }
      })
    : []);

  const storySection = homePage?.sections.find((s) => s.type === 'story');
  const storyText = override?.story || (typeof storySection?.content === 'string' ? storySection.content : '');

  const gridSection = homePage?.sections.find((s) => s.type === 'grid');
  const gridCards = override?.grid || (Array.isArray(gridSection?.content)
    ? gridSection.content.map((c) => {
        try {
          return JSON.parse(c);
        } catch {
          return { title: c, description: '', icon: 'ri-briefcase-line' };
        }
      })
    : []);

  const gallerySection = homePage?.sections.find((s) => s.type === 'gallery');
  const galleryImages = override?.gallery || (Array.isArray(gallerySection?.content) ? gallerySection.content : []);

  const blogPage = (pages && pages.find((p) => p.pageId === 'blog')) || pageContents.find((p) => p.pageId === 'blog');
  const blogPageSection = blogPage?.sections.find((s) => s.type === 'blog');
  const blogSection = homePage?.sections.find((s) => s.type === 'blog');
  
  // Use blog page posts if available, otherwise fallback to home page section content
  const sourceContent = blogPageSection?.content || blogSection?.content;
  
  const blogPosts = override?.blog || (Array.isArray(sourceContent)
    ? sourceContent.map((c) => {
        try {
          return typeof c === 'string' ? JSON.parse(c) : c;
        } catch {
          return null;
        }
      }).filter(Boolean)
    : []);

  const navPages = (pages && pages.length > 0)
    ? pages.map(p => ({ id: p.pageId, name: p.pageName, enabled: true }))
    : flattenPages(siteMapNodes).filter((n) => n.enabled);

  return (
    <div className="flex gap-3 h-full min-h-0">
      {/* Left Panel - Template Gallery */}
      <div className="w-[300px] bg-white rounded-xl border border-gray-200 flex-shrink-0 overflow-hidden flex flex-col">
        <div className="p-3 border-b border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-sm text-[#222222]">Choose a Template</h3>
            <button
              onClick={() => setTemplatePanelOpen(!templatePanelOpen)}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer"
              title={templatePanelOpen ? 'Collapse templates' : 'Expand templates'}
            >
              <i className={`ri-arrow-up-s-line text-sm ${templatePanelOpen ? '' : 'hidden'}`}></i>
              <i className={`ri-arrow-down-s-line text-sm ${templatePanelOpen ? 'hidden' : ''}`}></i>
            </button>
          </div>
          <div
            className={`flex flex-wrap gap-1 transition-all duration-300 overflow-hidden ${
              templatePanelOpen ? 'max-h-20 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`text-[10px] px-2.5 py-1 rounded-full border transition-all duration-150 cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#0859b8] text-white border-[#0859b8] shadow-sm'
                    : 'bg-white text-gray-500 border-gray-200 hover:bg-[#e8f9fb] hover:text-[#0b7f8f] hover:border-[#e8f9fb]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div
          className={`flex-1 overflow-y-auto p-3 space-y-2.5 transition-all duration-300 ${
            templatePanelOpen ? 'max-h-[calc(100vh-200px)] opacity-100' : 'max-h-0 opacity-0 p-0'
          }`}
        >
          {filteredTemplates.map(template => (
            <button
              key={template.id}
              onClick={() => {
                if (checkLock()) return;
                onSelectTemplate(template.id);
                // Update theme fonts based on template selection
                const next = { ...theme };
                let changed = false;
                if (template.headingFont && !overrides.has('headingFont')) {
                  next.headingFont = template.headingFont;
                  changed = true;
                }
                if (template.bodyFont && !overrides.has('bodyFont')) {
                  next.bodyFont = template.bodyFont;
                  changed = true;
                }
                if (colorSource === 'template' && template.palette) {
                  (['primary', 'accent', 'background', 'text', 'cta'] as const).forEach(key => {
                    if (!overrides.has(key)) {
                      next[key] = template.palette![key];
                      changed = true;
                    }
                  });
                }
                if (changed) {
                  onThemeChange(next);
                }
              }}
              className={`w-full text-left rounded-2xl border overflow-hidden transition-all duration-200 cursor-pointer ${
                selectedTemplate === template.id
                  ? 'bg-[#f7fbff] border-[#0859b8] shadow-sm'
                  : 'bg-white border-[#e7edf5] hover:border-[#dfe8f3] hover:-translate-y-0.5 hover:shadow-md'
              }`}
            >
              <div className="aspect-[16/9] overflow-hidden bg-gray-100">
                <img src={template.thumbnail} alt={template.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-medium text-gray-900">{template.name}</h4>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded border ${
                    template.category === 'Restaurant' ? 'bg-[#fff7e8] text-[#b26a00] border-[#f6ddb0]' :
                    template.category === 'Professional' || template.category === 'Clinic' ? 'bg-[#eef4fb] text-[#4c6a92] border-[#d7e3f4]' :
                    template.category === 'Salon' ? 'bg-[#fdf8e8] text-[#b7791f] border-[#f6e7b0]' :
                    'bg-[#f5f7fa] text-[#6b7280] border-[#e5e7eb]'
                  }`}>
                    {template.category}
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">{template.description}</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Right Panel - Theme + Preview */}
      <div className="flex-1 flex flex-col gap-3 overflow-hidden min-h-0">
        {/* Theme Panel */}
        <div className="bg-white rounded-xl border border-[#dfe8f3] p-4 flex-shrink-0 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm text-[#222222]">Theme Customisation</h3>
            <div className="flex items-center gap-2">
              <span className="text-[9px] text-[#4c6a92] bg-[#eef4fb] border border-[#d7e3f4] px-1.5 py-0.5 rounded">
                Auto-populated from onboarding
              </span>
              <button
                className="flex items-center gap-1 text-[9px] text-[#0b7f8f] hover:text-[#0b7f8f]/80 transition-colors cursor-pointer group relative"
                onClick={(e) => {
                  e.stopPropagation();
                  const t = document.getElementById('seo-tooltip');
                  if (t) {
                    t.classList.toggle('opacity-0');
                    t.classList.toggle('opacity-100');
                    setTimeout(() => {
                      t.classList.add('opacity-0');
                      t.classList.remove('opacity-100');
                    }, 4000);
                  }
                }}
              >
                <i className="ri-information-line text-[10px]"></i>
                <span>SEO auto-generated later</span>
                <div
                  id="seo-tooltip"
                  className="absolute right-0 top-full mt-1 w-52 bg-[#0859b8] text-white text-[10px] p-2.5 rounded-lg shadow-lg opacity-0 pointer-events-none transition-opacity z-50 leading-relaxed"
                >
                  Meta titles, descriptions, and URL slugs will be generated automatically once your keyword strategy is finalised.
                </div>
              </button>
            </div>
          </div>

          {/* Brand Identity */}
          <div className="mb-4 flex items-center gap-6 border-b border-gray-100 pb-4">
             <div className="flex-1 text-left">
                <label className="text-[10px] font-medium text-[#6b7280] uppercase tracking-wide mb-1.5 block">Brand Name</label>
                <input 
                  type="text" 
                  value={brandName}
                  onChange={(e) => {
                    if (checkLock()) return;
                    onBrandNameChange(e.target.value);
                  }}
                  className="w-full text-xs border border-[#dfe8f3] rounded-lg px-3 py-2 focus:border-[#0859b8] focus:ring-2 focus:ring-[#0859b8]/30 outline-none transition-colors bg-white font-medium shadow-sm"
                  placeholder="Enter brand name"
                />
             </div>
             <div className="flex-shrink-0 text-left">
                <label className="text-[10px] font-medium text-[#6b7280] uppercase tracking-wide mb-1.5 block">Logo</label>
                <div className="flex items-center gap-2">
                   <div className="w-9 h-9 rounded-lg border border-[#dfe8f3] bg-[#f5f7fa] flex items-center justify-center overflow-hidden">
                      {brandLogo ? <img src={brandLogo} className="w-full h-full object-contain" alt="logo" /> : <i className="ri-image-line text-[#a0aec0]"></i>}
                   </div>
                   <label className="text-[10px] bg-[#0859b8] border border-[#0859b8] text-white hover:bg-[#06489a] hover:border-[#06489a] px-3 py-2 rounded-lg cursor-pointer transition-colors font-medium shadow-sm">
                      Change
                      <input 
                        type="file" 
                        className="hidden" 
                        accept="image/*" 
                        onChange={(e) => {
                          if (checkLock()) return;
                          const file = e.target.files?.[0];
                          if (file) {
                            const reader = new FileReader();
                            reader.onload = (ev) => onBrandLogoChange(ev.target?.result as string);
                            reader.readAsDataURL(file);
                          }
                        }} 
                      />
                   </label>
                </div>
             </div>
          </div>

          {/* Colours */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-medium text-[#6b7280] uppercase tracking-wide block">Colours</label>
              <div className="flex bg-[#f5f7fa] border border-[#e7edf5] rounded-lg p-0.5">
                 <button 
                   onClick={() => handleColorSourceChange('brand')}
                   className={`text-[10px] px-3 py-1.5 rounded-md transition-all ${colorSource === 'brand' ? 'bg-[#0859b8] shadow-sm font-medium text-white' : 'text-[#6b7280] hover:text-[#222222] hover:bg-[#eef4fb]'}`}
                 >
                   <span className="flex items-center gap-1.5">
                     <span className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: brandTheme?.primary || '#1b2240' }}></span>
                     Brand Colors
                   </span>
                 </button>
                 <button 
                   onClick={() => handleColorSourceChange('template')}
                   className={`text-[10px] px-3 py-1.5 rounded-md transition-all ${colorSource === 'template' ? 'bg-[#0859b8] shadow-sm font-medium text-white' : 'text-[#6b7280] hover:text-[#222222] hover:bg-[#eef4fb]'}`}
                 >
                   <span className="flex items-center gap-1.5">
                     <span className="w-2 h-2 rounded-full shadow-sm" style={{ backgroundColor: templateList.find(t => t.id === selectedTemplate)?.palette?.primary || '#1b2240' }}></span>
                     Template Colors
                   </span>
                 </button>
              </div>
            </div>
            <div className="flex gap-3">
              {colorFields.map(({ key, label, fromOnboarding }) => (
                <div key={key} className="flex flex-col items-center gap-1">
                  <div className="relative">
                    <input
                      type="color"
                      value={theme[key] as string}
                      onChange={(e) => handleColorChange(key, e.target.value)}
                      className="w-8 h-8 rounded-full border border-[#dfe8f3] cursor-pointer overflow-hidden shadow-sm hover:shadow-md transition-shadow focus:ring-2 focus:ring-[#0859b8] focus:ring-offset-2"
                      style={{ background: theme[key] as string }}
                    />
                    {fromOnboarding && !overrides.has(key) && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-gold rounded-full border border-white" title="Extracted from onboarding"></span>
                    )}
                  </div>
                  <span className="text-[9px] text-gray-500">{label}</span>
                  <span className="text-[9px] text-gray-400 font-mono">{theme[key] as string}</span>
                  {fromOnboarding && !overrides.has(key) ? (
                    <button
                      onClick={() => handleColorChange(key, theme[key] as string)}
                      className="text-[9px] text-gold hover:text-gold-light cursor-pointer"
                    >
                      Override
                    </button>
                  ) : (
                    <span className="text-[9px] text-gray-400">Manual</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Typography */}
          <div>
            <label className="text-[10px] font-medium text-[#6b7280] uppercase tracking-wide mb-1.5 block">Typography</label>
            <div className="grid grid-cols-2 gap-3">
              {fontFields.map(({ key, label, fromOnboarding }) => (
                <div key={key}>
                  <div className="flex items-center gap-1 mb-1">
                    <label className="text-[10px] text-[#6b7280]">{label}</label>
                    {fromOnboarding && !overrides.has(key) && (
                      <span className="text-[8px] text-[#4c6a92] bg-[#eef4fb] border border-[#d7e3f4] px-1 py-0.5 rounded">Onboarding</span>
                    )}
                    {overrides.has(key) && (
                      <span className="text-[8px] text-[#5b7f87] bg-[#eef6f8] border border-[#d7ebef] px-1 py-0.5 rounded">Manual</span>
                    )}
                  </div>
                  <select
                    value={theme[key] as string}
                    onChange={(e) => handleColorChange(key, e.target.value)}
                    className="w-full text-xs border border-[#dfe8f3] rounded-lg px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-[#0859b8]/30 focus:border-[#0859b8] bg-white cursor-pointer transition-colors shadow-sm text-[#222222]"
                  >
                    {fontOptions.map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <p className="text-[10px] text-gray-500 mt-1" style={{ fontFamily: theme[key] as string }}>
                    The quick brown fox
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Live Preview */}
        <div className="h-[320px] rounded-xl border border-[#e7edf5] overflow-hidden flex flex-col min-h-0 bg-white shadow-sm">
          <div className="flex items-center justify-between px-3 py-2 border-b border-[#f1f5f9] flex-shrink-0">
            <h3 className="font-semibold text-sm text-[#222222]">Live Preview</h3>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">
                {templateList.find(t => t.id === selectedTemplate)?.name}
              </span>
              <button
                onClick={onShowPreview}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-gray-100 text-gray-500 hover:text-navy transition-colors cursor-pointer"
                title="Open full preview"
              >
                <i className="ri-external-link-line text-xs"></i>
              </button>
            </div>
          </div>
          <div
            className="flex-1 w-full overflow-hidden relative min-h-0"
            style={{ background: theme.background }}
          >
            <div className="h-full overflow-y-auto">
              <div
                style={{
                  fontFamily: `'${theme.bodyFont}', sans-serif`,
                  color: theme.text,
                }}
              >
                {/* Mini Navbar */}
                <nav className="px-3 py-1.5 flex items-center justify-between border-b" style={{ borderColor: `${theme.text}10`, background: '#ffffffcc' }}>
                  <div className="flex items-center gap-1.5">
                    {brandLogo ? (
                      <img src={brandLogo} alt="Logo" className="h-4 max-w-[80px] object-contain" />
                    ) : (
                      <>
                        <div className="w-4 h-4 rounded flex items-center justify-center flex-shrink-0" style={{ background: theme.primary }}>
                          <span className="text-white font-bold text-[6px]">{brandName.substring(0, 3).toUpperCase()}</span>
                        </div>
                        <span className="font-serif text-[9px] font-semibold truncate max-w-[80px]" style={{ color: theme.text, fontFamily: `'${theme.headingFont}', serif` }}>
                          {brandName}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {navPages.slice(0, 3).map((p) => (
                      <span key={p.id} className="text-[7px] font-medium hover:opacity-70 cursor-pointer whitespace-nowrap" style={{ color: theme.text }}>
                        {p.name}
                      </span>
                    ))}
                  </div>
                </nav>

                {/* Hero */}
                <div className="relative h-24 overflow-hidden">
                  {firstHero.image ? (
                    <img src={firstHero.image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.accent}33 100%)` }} />
                  )}
                  <div className="absolute inset-0 bg-black/40"></div>
                  <div className="absolute inset-0 flex items-center justify-center px-3">
                    <div className="text-center max-w-xs">
                      <h1 className="text-[10px] font-bold text-white leading-snug mb-0.5" style={{ fontFamily: `'${theme.headingFont}', serif` }}>
                        {firstHero.headline}
                      </h1>
                      {firstHero.sub && (
                        <p className="text-[7px] text-white/80 mb-1">{firstHero.sub}</p>
                      )}
                      <span className="inline-block text-[7px] font-medium px-2 py-0.5 rounded text-white cursor-pointer whitespace-nowrap" style={{ background: theme.cta }}>
                        {firstHero.cta}
                      </span>
                    </div>
                  </div>
                </div>

                {/* USP Strip */}
                {uspCards.length > 0 && (
                  <div className="py-2 px-3 grid grid-cols-3 gap-2">
                    {uspCards.slice(0, 3).map((card, idx) => (
                      <div key={idx} className="text-center py-2 px-1 rounded" style={{ background: `${theme.primary}06` }}>
                        <div className="w-4 h-4 mx-auto mb-1 rounded flex items-center justify-center" style={{ background: `${theme.accent}15` }}>
                          <i className={`${card.icon || 'ri-star-line'} text-[8px]`} style={{ color: theme.accent }}></i>
                        </div>
                        <span className="text-[7px] font-semibold block mb-0.5" style={{ color: theme.text, fontFamily: `'${theme.headingFont}', serif` }}>{card.title}</span>
                        <span className="text-[6px] leading-tight block" style={{ color: `${theme.text}80` }}>{card.description}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Brand Story */}
                {storyText && (
                  <div className="px-3 py-2 text-center" style={{ background: theme.primary }}>
                    <h2 className="text-[9px] font-bold text-white mb-0.5" style={{ fontFamily: `'${theme.headingFont}', serif` }}>{storySection?.title || 'Built for Founders'}</h2>
                    {storySection?.subtitle && storySection.subtitle.trim().toLowerCase() !== (storySection?.title || '').trim().toLowerCase() && (
                      <p className="text-[6px] text-white/80 leading-tight mb-1 max-w-xs mx-auto">{storySection.subtitle}</p>
                    )}
                    <p className="text-[7px] text-white/70 leading-relaxed max-w-xs mx-auto">{storyText}</p>
                  </div>
                )}

                {/* Services Grid */}
                {gridCards.length > 0 && (
                  <div className="py-2 px-3">
                    <div className="text-center mb-2">
                      <h2 className="text-[9px] font-bold mb-0.5" style={{ color: theme.text, fontFamily: `'${theme.headingFont}', serif` }}>{gridSection?.title || 'Services Designed to Scale'}</h2>
                      {gridSection?.subtitle && gridSection.subtitle.trim().toLowerCase() !== (gridSection?.title || '').trim().toLowerCase() && (
                        <p className="text-[6px] leading-tight" style={{ color: `${theme.text}80` }}>{gridSection.subtitle}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-1.5">
                      {gridCards.slice(0, 3).map((card, idx) => (
                        <div key={idx} className="rounded p-1.5 border" style={{ background: '#fff', borderColor: `${theme.text}10` }}>
                          <div className="w-3.5 h-3.5 rounded flex items-center justify-center mb-1" style={{ background: `${theme.accent}12` }}>
                            <i className={`${card.icon || 'ri-briefcase-line'} text-[7px]`} style={{ color: theme.accent }}></i>
                          </div>
                          <span className="text-[7px] font-semibold block mb-0.5" style={{ color: theme.text }}>{card.title}</span>
                          <span className="text-[6px] leading-tight block" style={{ color: `${theme.text}80` }}>{card.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Gallery */}
                {galleryImages.length > 0 && (
                  <div className="py-2 px-3">
                    <div className="text-center mb-1.5">
                      <h2 className="text-[9px] font-bold mb-0.5" style={{ color: theme.text, fontFamily: `'${theme.headingFont}', serif` }}>{gallerySection?.title || 'Our Work'}</h2>
                      {gallerySection?.subtitle && gallerySection.subtitle.trim().toLowerCase() !== (gallerySection?.title || '').trim().toLowerCase() && (
                        <p className="text-[6px] leading-tight" style={{ color: `${theme.text}80` }}>{gallerySection.subtitle}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-1">
                      {galleryImages.slice(0, 3).map((img, idx) => (
                        <div key={idx} className="aspect-[4/3] rounded overflow-hidden">
                          <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Blog */}
                {blogPosts.length > 0 && (
                  <div className="py-2 px-3" style={{ background: `${theme.primary}06` }}>
                    <div className="text-center mb-1.5">
                      <h2 className="text-[9px] font-bold mb-0.5" style={{ color: theme.text, fontFamily: `'${theme.headingFont}', serif` }}>{blogSection?.title || blogPageSection?.title || 'Latest from the Blog'}</h2>
                      {(blogSection?.subtitle || blogPageSection?.subtitle) && (blogSection?.subtitle || blogPageSection?.subtitle)?.trim().toLowerCase() !== (blogSection?.title || blogPageSection?.title || '').trim().toLowerCase() && (
                        <p className="text-[6px] leading-tight" style={{ color: `${theme.text}80` }}>{blogSection?.subtitle || blogPageSection?.subtitle}</p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {blogPosts.slice(0, 2).map((post, idx) => (
                        <div key={idx} className="rounded overflow-hidden border" style={{ background: '#fff', borderColor: `${theme.text}10` }}>
                          <div className="h-10 flex items-center justify-center overflow-hidden" style={{ background: `${theme.primary}0a` }}>
                            {post.image ? (
                              <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
                            ) : (
                              <i className="ri-article-line text-base" style={{ color: `${theme.primary}33` }}></i>
                            )}
                          </div>
                          <div className="p-1.5">
                            <span className="text-[6px] font-medium uppercase tracking-wide" style={{ color: theme.accent }}>{post.date}</span>
                            <h3 className="text-[7px] font-semibold mt-0.5 mb-0.5 truncate" style={{ color: theme.text }}>{post.title}</h3>
                            <p className="text-[6px] leading-tight line-clamp-2" style={{ color: `${theme.text}80` }}>{post.excerpt}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact */}
                <div className="py-2 px-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <h2 className="text-[9px] font-bold mb-0.5" style={{ color: theme.text, fontFamily: `'${theme.headingFont}', serif` }}>Let Us Talk</h2>
                      <p className="text-[7px] leading-relaxed mb-1" style={{ color: `${theme.text}80` }}>Reach out and our team will get back within 24 hours.</p>
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1 text-[7px]" style={{ color: theme.text }}>
                          <i className="ri-mail-line text-[8px]" style={{ color: theme.accent }}></i>
                          <span>{globalContactInfo.email}</span>
                        </div>
                        <div className="flex items-center gap-1 text-[7px]" style={{ color: theme.text }}>
                          <i className="ri-phone-line text-[8px]" style={{ color: theme.accent }}></i>
                          <span>{globalContactInfo.phone}</span>
                        </div>
                      </div>
                    </div>
                    <div className="rounded p-1.5 border" style={{ background: '#fff', borderColor: `${theme.text}10` }}>
                      <div className="grid grid-cols-2 gap-1 mb-1">
                        <input type="text" placeholder="Name" className="text-[7px] px-1 py-0.5 rounded border focus:outline-none" style={{ borderColor: `${theme.text}10` }} />
                        <input type="email" placeholder="Email" className="text-[7px] px-1 py-0.5 rounded border focus:outline-none" style={{ borderColor: `${theme.text}10` }} />
                      </div>
                      <textarea placeholder="Message" rows={2} className="w-full text-[7px] px-1 py-0.5 rounded border focus:outline-none resize-none mb-1" style={{ borderColor: `${theme.text}10` }} />
                      <button className="w-full text-[7px] font-medium px-1.5 py-0.5 rounded text-white cursor-pointer whitespace-nowrap" style={{ background: theme.cta }}>
                        Send
                      </button>
                    </div>
                  </div>
                </div>

                {/* Footer */}
                <footer className="px-3 py-1.5 border-t flex items-center justify-between" style={{ background: theme.primary, borderColor: `${theme.text}10` }}>
                  <div className="flex items-center gap-1.5">
                    {brandLogo ? (
                      <img src={brandLogo} alt="Logo" className="h-3 max-w-[60px] object-contain bg-white rounded p-0.5" />
                    ) : (
                      <>
                        <div className="w-3 h-3 rounded flex items-center justify-center flex-shrink-0" style={{ background: theme.accent }}>
                          <span className="text-white font-bold text-[5px]">{brandName.substring(0, 3).toUpperCase()}</span>
                        </div>
                        <span className="font-serif text-[7px] font-semibold text-white truncate max-w-[70px]">{brandName}</span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    {navPages.slice(0, 3).map((p) => (
                      <span key={p.id} className="text-[6px] text-white/60 hover:text-white cursor-pointer whitespace-nowrap">{p.name}</span>
                    ))}
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-twitter-x-line text-[7px] text-white/40"></i>
                    <i className="ri-linkedin-box-line text-[7px] text-white/40"></i>
                    <i className="ri-instagram-line text-[7px] text-white/40"></i>
                  </div>
                </footer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
